// Schemes a doclet link may use. Anything else (javascript:, data:, ...) is
// refused: doclets are shown to people outside World 50.
const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:']
// Schemes whose link is an address after the colon, not a host
const ADDRESS_PROTOCOLS = ['mailto:', 'tel:']

const SCHEME = /^[a-z][a-z\d+.-]*:/i
// "localhost:3000/x" or "world50.com:8080": a host and port, not a scheme
const HOST_AND_PORT = /^[^/:\s]+:\d+(\/|$)/
// A web link's host must look like a real address: a domain with a dot and
// a proper ending ("world50.com"), an IP address, or localhost. A single word
// such as "blue" parses as a host, but goes nowhere for a doclet's readers.
// The ending is letters ("com", "org"), or an internationalised one ("xn--...")
const DOMAIN = /^([a-z\d-]+\.)+([a-z]{2,}|xn--[a-z\d-]+)$/i
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/
const isRealHost = (hostname: string) =>
  hostname === 'localhost' ||
  hostname.startsWith('[') || // IPv6
  IPV4.test(hostname) ||
  DOMAIN.test(hostname)

// A bare email address, typed as a link
const EMAIL = /^[^\s@/:]+@[^\s@/:]+\.[^\s@/:]+$/

/**
 * A link as typed or stored, made safe to use as an href, or null if it's
 * empty, refused or not a usable link.
 *
 * First it reads what was meant: a bare email address becomes a mailto link,
 * a path or #anchor is refused (it has no site to belong to once the doclet is
 * shared), and a link without a scheme gets https -- so "world50.com" goes to
 * the site, not somewhere relative to MI6. Then the browser's URL parser
 * checks it: malformed links and hosts that aren't real addresses ("blue")
 * are refused, only http, https, mailto and tel pass (checked after parsing, so case and stray characters can't sneak
 * another scheme through), and the normalised href is returned.
 */
export const safeLink = (link: string | null | undefined): string | null => {
  const value = (link ?? '').trim()
  if (!value) return null

  let candidate: string
  if (EMAIL.test(value)) {
    candidate = `mailto:${value}`
  } else if (value.startsWith('#') || /^\/(?!\/)/.test(value)) {
    return null
  } else if (value.startsWith('//')) {
    candidate = `https:${value}`
  } else if (SCHEME.test(value) && !HOST_AND_PORT.test(value)) {
    candidate = value
  } else {
    candidate = `https://${value}`
  }

  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return null
  }

  if (!ALLOWED_PROTOCOLS.includes(url.protocol)) return null
  if (ADDRESS_PROTOCOLS.includes(url.protocol)) {
    if (!url.pathname) return null
  } else if (!isRealHost(url.hostname)) {
    return null
  }
  return url.href
}
